/**
 * client/crazygames.ts
 * Responsabilidad: Wrapper del SDK v3 de CrazyGames.
 *
 * Solo se activa cuando el juego corre en un dominio de CrazyGames (o localhost con flag ?crazygames_sandbox=1).
 * En cualquier otro entorno (como Railway o localhost normal), degrada inmediatamente sin inicializar ni ejecutar nada.
 */

export interface CrazyGamesSDK {
    init: () => Promise<void>;
    user?: {
        isUserAccountAvailable: boolean;
        systemInfo: { countryCode: string; device: { type: string } };
    };
    ad?: {
        hasAdblock: () => Promise<boolean>;
        requestAd: (type: "midgame" | "rewarded", callbacks: any) => void;
    };
    game?: {
        gameplayStart: () => void;
        gameplayStop: () => void;
        loadingStart: () => void;
        loadingStop: () => void;
        isInstantMultiplayer?: boolean;
        getInviteParam?: (key: string) => string | null | undefined;
        inviteLink?: (params: { roomId: string }) => string;
        showInviteButton?: (params: { roomId: string }) => string;
        hideInviteButton?: () => void;
        addJoinRoomListener?: (cb: (params: { roomId?: string }) => void) => void;
    };
}

declare global {
    interface Window {
        CrazyGames?: { SDK: CrazyGamesSDK };
    }
}

/**
 * Detecta si el cliente está ejecutándose dentro del ecosistema CrazyGames.
 * Comprueba dominios oficiales y query param de sandbox local.
 */
export function isCrazyGamesEnvironment(): boolean {
    if (typeof window === "undefined") return false;

    // 1. Por hostname directo
    const host = window.location.hostname;
    if (host.includes("crazygames.com") || host.includes("crazygames.nl")) return true;

    // 2. Por estar dentro de un iframe (CrazyGames QA usa iframes)
    try {
        if (window.parent !== window) return true;
    } catch {
        return true;
    }

    // 3. Por referrer (si el iframe no expone el hostname)
    if (typeof document !== "undefined" && document.referrer && document.referrer.includes("crazygames")) return true;

    // 4. Sandbox local
    if ((host === "localhost" || host === "127.0.0.1") && window.location.search.includes("crazygames_sandbox=1")) return true;

    return false;
}

let sdkInstance: CrazyGamesSDK | null = null;

/**
 * Inicializa el SDK de CrazyGames v3 de forma segura y asíncrona si el entorno corresponde.
 */
export async function initCrazyGames(): Promise<CrazyGamesSDK | null> {
    if (sdkInstance) return sdkInstance;

    if (!isCrazyGamesEnvironment()) {
        console.log("[CrazyGames] Not in CrazyGames environment, skipping SDK");
        return null;
    }

    // Esperar a que el script async cargue (max 5s)
    let attempts = 0;
    const maxAttempts = 50; // 50 * 100ms = 5s
    while (typeof window.CrazyGames?.SDK === "undefined" && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
    }

    if (typeof window.CrazyGames?.SDK === "undefined") {
        console.warn("[CrazyGames] SDK script not loaded after 5s. Ensure the script tag is present in index.html");
        return null;
    }

    const sdk = window.CrazyGames.SDK;
    try {
        await sdk.init();
        sdkInstance = sdk;
        console.log("[CrazyGames] SDK initialized");
        return sdk;
    } catch (err) {
        console.error("[CrazyGames] SDK init failed:", err);
        return null;
    }
}

export function getCrazyGamesSDK(): CrazyGamesSDK | null {
    return sdkInstance;
}

/** Helper: invoca gameplayStart en CrazyGames SDK */
export function crazyGameplayStart(): void {
    if (sdkInstance?.game?.gameplayStart) {
        sdkInstance.game.gameplayStart();
    }
}

/** Helper: invoca gameplayStop en CrazyGames SDK */
export function crazyGameplayStop(): void {
    if (sdkInstance?.game?.gameplayStop) {
        sdkInstance.game.gameplayStop();
    }
}

/**
 * Devuelve true si la sesión fue iniciada mediante el botón "Play with Friends"
 * (Instant Multiplayer) de CrazyGames.
 */
export function isCrazyGamesInstantMultiplayer(): boolean {
    if (!sdkInstance?.game) return false;
    return Boolean(sdkInstance.game.isInstantMultiplayer);
}

/**
 * Obtiene el parámetro `roomId` del enlace de invitación de CrazyGames (`getInviteParam`)
 * o desde la URL (`?roomId=...`) para pruebas en sandbox local.
 */
export function getCrazyGamesInviteRoomId(): string | null {
    try {
        if (sdkInstance?.game && typeof sdkInstance.game.getInviteParam === "function") {
            const roomId = sdkInstance.game.getInviteParam("roomId");
            if (typeof roomId === "string" && roomId.trim().length > 0) {
                return roomId.trim().slice(0, 32);
            }
        }
    } catch (err) {
        console.warn("[CrazyGames] Error al leer getInviteParam('roomId'):", err);
    }

    try {
        const params = new URLSearchParams(window.location.search);
        const fallbackRoomId = params.get("roomId");
        if (fallbackRoomId && fallbackRoomId.trim().length > 0) {
            return fallbackRoomId.trim().slice(0, 32);
        }
    } catch {
        // Ignorar errores de URLSearchParams
    }
    return null;
}

/**
 * Genera el enlace oficial de invitación de CrazyGames con `sdk.game.inviteLink({ roomId })`.
 */
export function createCrazyGamesInviteLink(roomId: string): string | null {
    try {
        if (sdkInstance?.game && typeof sdkInstance.game.inviteLink === "function") {
            const link = sdkInstance.game.inviteLink({ roomId });
            if (typeof link === "string" && link.length > 0) {
                return link;
            }
        }
    } catch (err) {
        console.warn("[CrazyGames] Error en inviteLink:", err);
    }
    // Fallback para pruebas locales fuera del iframe de CrazyGames
    try {
        return `${window.location.origin}${window.location.pathname}?roomId=${encodeURIComponent(roomId)}`;
    } catch {
        return null;
    }
}

/**
 * Muestra el botón oficial flotante "Invite Friends" de CrazyGames mientras se está en partida.
 */
export function showCrazyGamesInviteButton(roomId: string): void {
    if (!sdkInstance?.game || typeof sdkInstance.game.showInviteButton !== "function") return;
    try {
        sdkInstance.game.showInviteButton({ roomId });
    } catch (err) {
        console.warn("[CrazyGames] Error en showInviteButton:", err);
    }
}

/**
 * Oculta el botón flotante de invitación de CrazyGames (al abrir menús o morir).
 */
export function hideCrazyGamesInviteButton(): void {
    if (!sdkInstance?.game || typeof sdkInstance.game.hideInviteButton !== "function") return;
    try {
        sdkInstance.game.hideInviteButton();
    } catch {
        // Ignorar
    }
}

/**
 * Registra el listener `addJoinRoomListener` exigido por CrazyGames por si el jugador
 * acepta una invitación mientras el juego ya está abierto.
 */
export function registerCrazyGamesJoinListener(onJoinRoom: (roomId: string) => void): void {
    if (!sdkInstance?.game || typeof sdkInstance.game.addJoinRoomListener !== "function") return;
    try {
        sdkInstance.game.addJoinRoomListener((params) => {
            if (params && typeof params.roomId === "string" && params.roomId.trim().length > 0) {
                onJoinRoom(params.roomId.trim().slice(0, 32));
            }
        });
    } catch (err) {
        console.warn("[CrazyGames] Error registrando addJoinRoomListener:", err);
    }
}

